import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStock } from "@/context/StockContext";
import { ProductSubcategory, ProductCategory } from "@/types/stock";
import { Plus, Search, MoreHorizontal, Edit, Trash2, Tag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import SubcategoryFormDialog from "./SubcategoryFormDialog";
import StockPasswordDialog from "./StockPasswordDialog";

interface SubcategoryManagementProps {
  viewMode?: "grid" | "list";
  touchMode?: boolean;
  onViewModeChange?: (mode: "grid" | "list") => void;
}

const SubcategoryManagement: React.FC<SubcategoryManagementProps> = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    subcategories,
    categories,
    createSubcategory,
    updateSubcategory,
    deleteSubcategory,
    loading,
  } = useStock();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] =
    useState<ProductSubcategory | null>(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<() => void>(() => {});

  // Filter subcategories based on search term and selected category
  const filteredSubcategories = subcategories.filter((subcategory) => {
    const matchesSearch =
      subcategory.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subcategory.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || subcategory.categoryId === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleCreateSubcategory = () => {
    setPendingAction(() => () => {
      setEditingSubcategory(null);
      setIsFormOpen(true);
    });
    setIsPasswordDialogOpen(true);
  };

  const handleEditSubcategory = (subcategory: ProductSubcategory) => {
    setPendingAction(() => () => {
      setEditingSubcategory(subcategory);
      setIsFormOpen(true);
    });
    setIsPasswordDialogOpen(true);
  };

  const handleDeleteSubcategory = async (subcategory: ProductSubcategory) => {
    setPendingAction(() => async () => {
      try {
        await deleteSubcategory(subcategory.id);
        toast({
          title: t("stock.subcategory.deleted"),
          description: t("stock.subcategory.deletedDescription"),
        });
      } catch (error) {
        toast({
          title: t("common.error"),
          description: t("stock.subcategory.deleteError"),
          variant: "destructive",
        });
      }
    });
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordSuccess = () => {
    setIsPasswordDialogOpen(false);
    pendingAction();
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || t("common.unknown");
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            {t("stock.subcategories")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder={t("stock.subcategory.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder={t("stock.category.selectCategory")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all")}</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleCreateSubcategory}
              className="w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("stock.subcategory.add")}
            </Button>
          </div>

          {/* Subcategories Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("stock.subcategory.name")}</TableHead>
                  <TableHead>{t("stock.category.name")}</TableHead>
                  <TableHead>{t("stock.subcategory.description")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead className="text-right">
                    {t("common.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      {t("common.loading")}
                    </TableCell>
                  </TableRow>
                ) : filteredSubcategories.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-8 text-gray-500"
                    >
                      {searchTerm || selectedCategory !== "all"
                        ? t("stock.subcategory.noResults")
                        : t("stock.subcategory.empty")}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubcategories.map((subcategory) => (
                    <TableRow key={subcategory.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {subcategory.icon && (
                            <span className="text-lg">{subcategory.icon}</span>
                          )}
                          <span>{subcategory.name}</span>
                          {subcategory.color && (
                            <div
                              className="w-3 h-3 rounded-full border"
                              style={{ backgroundColor: subcategory.color }}
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getCategoryName(subcategory.categoryId)}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {subcategory.description || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            subcategory.isActive ? "default" : "secondary"
                          }
                        >
                          {subcategory.isActive
                            ? t("common.active")
                            : t("common.inactive")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleEditSubcategory(subcategory)}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              {t("common.edit")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                handleDeleteSubcategory(subcategory)
                              }
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t("common.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <SubcategoryFormDialog
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingSubcategory(null);
        }}
        subcategory={editingSubcategory}
        onSubmit={async (data) => {
          try {
            if (editingSubcategory) {
              await updateSubcategory(editingSubcategory.id, data);
              toast({
                title: t("stock.subcategory.updated"),
                description: t("stock.subcategory.updatedDescription"),
              });
            } else {
              await createSubcategory(data);
              toast({
                title: t("stock.subcategory.created"),
                description: t("stock.subcategory.createdDescription"),
              });
            }
            setIsFormOpen(false);
            setEditingSubcategory(null);
          } catch (error) {
            toast({
              title: t("common.error"),
              description: editingSubcategory
                ? t("stock.subcategory.updateError")
                : t("stock.subcategory.createError"),
              variant: "destructive",
            });
          }
        }}
      />

      {/* Password Dialog */}
      <StockPasswordDialog
        isOpen={isPasswordDialogOpen}
        onClose={() => setIsPasswordDialogOpen(false)}
        onSuccess={handlePasswordSuccess}
      />
    </div>
  );
};

export default SubcategoryManagement;
